import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Finance & accounting",
  title: "Bank reconciliation agent",
  cardSummary:
    "Reconcile the bank statement against your cash book line by line — matching both directions, posting bank charges, explaining timing differences, and flagging genuine discrepancies.",
  heroSubhead:
    "We design, build and deploy an AI agent that reconciles your bank statement against the cash book or general ledger — matching every line both ways, auto-posting bank-only items like charges and interest, explaining timing differences such as unpresented cheques, and producing a reconciliation that balances to zero — surfacing only the genuine discrepancies for your controller to resolve.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Month-end close stops waiting on a manual reconciliation, bank charges and errors are caught the same day, and an unexplained difference — the thing that hides a missing entry, a duplicate, or fraud — is investigated instead of forced to balance.",
    complexityLabel: "Medium–High",
    complexityWhy:
      "Two-sided matching across varied statement formats and the cash book, timing-difference logic, and it posts bank-only items to the GL — so the balancing bar, idempotency and the eval bar matter more than a one-way flagger, though a human still approves the postings.",
    timeToLive: "~a few weeks (one bank account / entity, shadow mode first)",
    bestFit: "Finance teams reconciling one or many bank accounts to the ledger daily or at month-end",
  },
  outcomes: [
    "Month-end closes faster — the reconciliation is ready each morning, not a two-day scramble at period end",
    "Errors and fraud surface — a genuine unexplained difference is investigated, not forced to balance to hit a deadline",
    "Bank charges, interest and FX posted automatically — no more chasing small bank-only entries by hand",
    "Every account reconciled consistently — the same checks on every statement, across all your bank accounts",
  ],
  impact: {
    manualLabel: "Reconciling an account by hand",
    manualValue: "~30–90 min per account",
    agentLabel: "With the agent",
    agentValue: "~5 min review (exceptions only)",
    saved:
      "≈ 25–85 minutes per account each cycle — and across many accounts at month-end that's days of controller time back — while a single caught error, duplicate payment or fraudulent debit can outweigh the whole AI cost for the month.",
    note: "Indicative; the actual time and match rate depend on the number of accounts, transaction volume, how clean your cash-book references are, and how many bank-only items recur.",
  },
  businessProblem:
    "Finance downloads each bank statement, opens the cash book or general ledger, and ticks off every line — matching receipts and payments both ways, spotting the bank charges and interest that were never booked, working out which cheques haven't cleared yet and which deposits are still in transit, and chasing any difference that won't balance. Across many accounts at month-end it's a slog, and under deadline pressure the temptation is to plug the last unexplained difference to a suspense line just to make it balance. That plug is exactly how a missing entry, a duplicate payment, or a fraudulent debit gets buried — the reconciliation is meant to be the control that catches them, and manual, rushed reconciliation is where the control quietly fails.",
  manualWorkflow: {
    steps: [
      "Download each bank statement and open the cash book / GL",
      "Tick off matching lines both ways — receipts and payments",
      "Spot bank-only items — charges, interest, FX — never booked",
      "Work out timing differences — unpresented cheques, deposits in transit",
      "Chase and explain any difference that won't balance",
      "Post the adjustments and file the reconciliation statement",
    ],
    breaks:
      "The slow, thankless part is ticking hundreds of lines and hunting the last stubborn difference — and under deadline that difference gets plugged to a suspense account just to balance, which is exactly how a missing entry or a fraudulent debit stays hidden.",
  },
  agenticWorkflow: {
    narrative:
      "We build it as an evaluator-optimizer workflow: the agent matches the statement to the cash book, then evaluates the one thing that matters — does the reconciliation balance to zero, with every residual explained? If a difference is unexplained, it loops back to re-match, post the bank-only items and classify timing differences, refining until the statement balances or what's left is a named, evidenced exception for a human — never a plug.",
    steps: [
      "Ingest the statement and the cash book / GL",
      "Match every line both ways",
      "Post bank-only items; classify timing differences",
      "Evaluate: does it balance? explain every residual",
      "Produce the reconciliation; flag real discrepancies",
    ],
    inLabel: "A statement + the cash book arrive",
    outLabel: "A balanced reconciliation + flagged exceptions",
    loopAround: {
      index: 3,
      label:
        "If the reconciliation doesn't balance, it loops back to re-match, hunt the missing or duplicated entry and re-classify timing differences — refining until it balances or the residual is a named exception, never plugged to suspense.",
    },
    humanStep: "Your controller reviews the exceptions and approves the postings",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Statement vs cash book",
    inMessage:
      "Bank statement closing balance RM 128,540.00. Cash book balance RM 131,905.00 — a RM 3,365 difference. 42 statement lines, 41 cash-book lines this month.",
    outLabel: "How the agent reconciles it",
    outLines: [
      "40 lines matched both ways; the RM 3,365 gap traced to 3 reconciling items",
      "Deposit in transit RM 3,300 — banked 30 Mar, not yet on the statement; timing difference, not an error",
      "Bank charges RM 105 and interest RM 40 — bank-only, drafted as GL postings for approval",
      "Bridge: 131,905 − 105 + 40 − 3,300 = 128,540; residual RM 0.00",
    ],
    outResult:
      "Balanced to RM 0 — the RM 3,365 gap fully explained: RM 3,300 timing + RM 65 net charges/interest posted. Nothing plugged.",
    note: "Every reconciling item cites the statement line and cash-book entry · postings approved by your controller · ~RM 0.15–0.40 per account per cycle in AI cost.",
  },
  workflowDetail: [
    {
      title: "Ingest the statement and the cash book",
      detail:
        "The agent takes the bank statement in whatever form it arrives — a bank feed (CAMT/MT940), a CSV, or a PDF — and the cash-book or GL entries for the period, normalising both into a common shape: date, amount, direction, counterparty and reference, reading PDFs with vision where needed.",
    },
    {
      title: "Match every line, both directions",
      detail:
        "It matches statement lines to cash-book entries in both directions — receipts and payments — exact where it can and fuzzy on date, amount and reference where it can't, so a payment recorded a day early or under a slightly different reference still ties out.",
    },
    {
      title: "Post bank-only items and classify timing differences",
      detail:
        "For lines on the statement but not the books — bank charges, interest, FX, transfers — it drafts a GL posting for approval. For lines in the books but not yet on the statement — unpresented cheques, deposits in transit — it classifies them as timing differences and carries them forward, not as errors.",
    },
    {
      title: "Evaluate and refine (the loop)",
      detail:
        "It checks the one thing that matters: does the reconciliation now balance to zero with every residual explained? If not, it loops back — re-matching, hunting a missing or duplicated entry, re-testing a timing classification — refining until it balances or the difference is isolated to a named exception.",
    },
    {
      title: "Produce the reconciliation, flag discrepancies",
      detail:
        "It outputs the reconciliation statement — the bridge from cash-book balance to bank balance with every reconciling item evidenced — and a short list of genuine discrepancies to look at: an unmatched debit, a duplicate, a wrong-amount match, anything out of pattern.",
    },
    {
      title: "Hand to a human",
      detail:
        "Your controller reviews the flagged discrepancies and approves the drafted postings. Nothing is posted to the live ledger automatically, and no difference is ever plugged to force a balance.",
    },
  ],
  howWeBuild:
    "It reconciles to zero or it doesn't finish — where a difference can't be explained it surfaces a named exception rather than plugging it to a suspense line to force a balance, which is the whole point of the control. It works from your live cash book and the actual statement, matching against real entries, and it posts bank-only items only as a draft your controller approves, each carrying an idempotency key so re-running a statement can't double-post charges or interest. Every reconciling item — a matched line, a timing difference, or a flagged discrepancy — cites the exact statement line and cash-book entry it came from, so the reconciliation is reconstructable end to end.",
  agentLoop: [
    "the statement + your live cash book / GL for the period",
    "match both ways, post bank-only items & classify residuals (MCP tools)",
    "re-check the reconciliation balances to zero before it finishes",
  ],
  integrations: [
    "Bank statement import (CAMT / MT940, CSV or PDF) across your accounts",
    "General ledger / cash book (your accounting system)",
    "Optional: the cash-application feed, so applied customer receipts match cleanly",
  ],
  architecture: [
    "Pattern: an evaluator-optimizer loop on the Claude Agent SDK — match → evaluate 'does it balance and is every residual explained?' → refine — with each tool exposed via an in-process MCP server.",
    "Context: the actual bank statement and the live cash-book / GL entries for the period are loaded per run, so matching is against real, current entries and the reconciliation reflects today's books rather than an export.",
    "Tools: read-only statement and cash-book lookups, a two-way line matcher (exact plus fuzzy on date/amount/reference), a timing-difference classifier (unpresented cheques, deposits in transit), and one write tool that only drafts GL postings for bank-only items — no tool can plug a suspense balance or edit a matched entry.",
    "The balancing gate: the run completes only when the reconciliation balances to zero with every residual explained, or when the unexplained difference is isolated to a named, evidenced exception — it is structurally unable to force a balance by plugging.",
    "Idempotency: every GL posting carries an idempotency key (statement-line ID + amount + date) so re-running a statement or a re-import can't double-post charges, interest or FX.",
    "Guardrails: least-privilege — the agent drafts postings and a controller approves; it cannot move money, delete entries, or write outside the bank-only posting set; schema-locked output.",
    "Fraud & error surfacing: unmatched debits, duplicates, wrong-amount matches and out-of-pattern items are raised as discrepancies with evidence, never absorbed into a balancing figure — the control's real purpose.",
    "Observability & audit: every match, timing classification, posting and the final reconciliation statement is logged and traceable to its source lines — a machine-readable audit trail for Finance controls and external audit.",
    "Eval harness: a golden set of real historical reconciliations — clean, timing-heavy, and seeded with errors, duplicates and fraudulent debits — with deterministic balance checks plus an LLM-as-judge on the fuzzy matches, regression-tested before it posts to a live GL.",
  ],
  models: [
    {
      task: "Match & reconcile statement to cash book",
      model: "Claude Sonnet",
      why: "The core two-way reasoning across dates, amounts and messy references, at moderate cost.",
    },
    {
      task: "High-volume exact-line matching & normalisation",
      model: "Claude Haiku",
      why: "Most lines match exactly; cheapest and fastest for the bulk and for parsing statement formats.",
    },
    {
      task: "Classify timing differences & bank-only items",
      model: "Claude Sonnet",
      why: "Deciding unpresented-cheque vs deposit-in-transit vs a genuine miss needs real reasoning.",
    },
    {
      task: "Hunt stubborn unexplained differences / suspected fraud",
      model: "Claude Opus",
      why: "Invoked only when the reconciliation won't balance after refinement — a small fraction of runs.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per account, per reconciliation",
        value: "~RM 0.15–0.40",
        note: "statement + cash-book context + two-way matching + balancing loop + postings, at Claude Sonnet/Haiku rates",
      },
      {
        label: "AI usage — 50 accounts reconciled daily",
        value: "~RM 260–700 / month",
        note: "lower with the Batch API for overnight month-end runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "connecting your bank feeds and GL is a few weeks; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to a single caught duplicate payment or fraudulent debit a month",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on the number of accounts, transaction volume and how many timing differences recur. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  modelChoice: {
    approach: "Single model, tiered by task",
    rationale:
      "A reconciliation shares one context — the statement and the cash book for that account and period — so a single well-instrumented model runs the matching and the balancing loop, with cheaper Haiku on the many exact matches and Opus reserved only for a difference that won't resolve. Reliable, cheap per account, and easy to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single account's reconciliation.",
  },
  evaluation: [
    "Deterministic checks first — does it balance to zero, is every reconciling item evidenced, do the postings equal the bank-only total",
    "Self-verification — the agent won't finish on an unexplained difference; it loops or flags, and never plugs to suspense",
    "Business KPIs — time per reconciliation, share auto-reconciled, unexplained-difference rate, days to close",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure your current reconciliation on a sample — time per account, how consistently two accountants reconcile the same account, and how often a difference gets plugged to suspense today. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reconcile correctly?",
        metrics: [
          {
            name: "Reconciliation vs a senior GL accountant",
            measures:
              "Its matched set, reconciling items and flagged discrepancies agree with an expert's on held-out accounts",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the reconciliation sound and complete?",
        metrics: [
          {
            name: "Balances or isolates — never plugs",
            measures: "It reaches a zero balance with every residual explained, or isolates a named exception",
            target: "100%",
          },
          {
            name: "Required checks executed",
            measures: "Both-way match, bank-only posting and timing classification all run — nothing skipped",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Line-match accuracy",
            measures: "Matched statement and cash-book lines are genuinely the same transaction",
            target: "≥ 99%",
          },
          {
            name: "Discrepancy recall (catch rate)",
            measures: "Of the seeded errors, duplicates and fraudulent debits, how many it flags",
            target: "≥ 98%",
          },
          {
            name: "Posting accuracy",
            measures: "Bank-only postings are the right amount, sign and GL account",
            target: "≥ 99%",
          },
          {
            name: "No forced balances",
            measures: "An unexplained difference is never absorbed into a plug to make it balance",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not post to a live GL until it beats the human baseline and clears these GPA targets on a golden set of historical reconciliations — including timing-heavy months and seeded errors, duplicates and fraudulent debits — running in shadow mode first. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical statements and cash book — including the timing-heavy months and, seeded in, the errors and duplicates — running in shadow mode so you can see its reconciliations before anything is posted. A focused single-account rollout is a matter of weeks.",
  ctaTitle: "Still reconciling bank accounts by hand?",
  ctaDescription:
    "Tell us how many accounts you reconcile, your banks and your accounting system, and we'll tell you honestly whether a bank reconciliation agent is worth building — and how much it could auto-reconcile.",
  faqs: [
    {
      question: "How is this different from the cash application agent?",
      answer:
        "The cash application agent matches incoming customer receipts to open invoices (the accounts-receivable side). The bank reconciliation agent reconciles the whole bank statement against your cash book or GL — both directions, bank charges and interest, timing differences — and produces the reconciliation statement. They're complementary: the cash-application agent can feed clean applied receipts into the reconciliation.",
    },
    {
      question: "Does it move money or change our ledger on its own?",
      answer:
        "No. It drafts bank-only postings — charges, interest, FX — for a controller to approve, and it never moves money, deletes entries, or plugs a suspense balance. A human approves every posting; the agent's job is to reconcile and evidence, not to write to the ledger unsupervised.",
    },
    {
      question: "What happens when it can't make it balance?",
      answer:
        "It won't force it. An unexplained difference is isolated and surfaced as a named, evidenced exception — the missing entry, the duplicate, the suspicious debit — which is exactly the point of the control. It never plugs a difference to suspense to hit a deadline.",
    },
    {
      question: "How does it handle unpresented cheques and deposits in transit?",
      answer:
        "It classifies them as timing differences, not errors — showing them on the reconciliation and carrying them forward until they clear, kept distinct from a genuine discrepancy that needs action.",
    },
    {
      question: "Does it work across Malaysia and Singapore, multiple banks and currencies?",
      answer:
        "Yes. It reconciles multiple bank accounts, in RM and SGD, across varied statement formats (CAMT/MT940/CSV/PDF); FX differences are reconciled with the booked rate and a tolerance for charges. Treat tax and audit specifics as a starting point for your finance team, not accounting advice.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept on one account is usually a matter of weeks, running in shadow mode first; a full rollout across accounts takes longer, mostly bank-feed and GL integration and getting your finance team comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Kewangan & perakaunan",
  title: "Ejen rekonsiliasi bank",
  cardSummary:
    "Rekonsiliasi penyata bank dengan buku tunai anda baris demi baris — memadan dua hala, mencatat caj bank, menjelaskan perbezaan masa, dan menandakan percanggahan sebenar.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang merekonsiliasi penyata bank anda dengan buku tunai atau lejar am — memadan setiap baris dua hala, mencatat automatik item bank-sahaja seperti caj dan faedah, menjelaskan perbezaan masa seperti cek belum tunai, dan menghasilkan rekonsiliasi yang seimbang kepada sifar — hanya memaparkan percanggahan sebenar untuk pengawal anda selesaikan.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Penutupan hujung bulan berhenti menunggu rekonsiliasi manual, caj bank dan ralat ditangkap pada hari sama, dan perbezaan tidak dijelaskan — perkara yang menyembunyikan entri hilang, pendua, atau penipuan — disiasat dan bukan dipaksa seimbang.",
    complexityLabel: "Sederhana–Tinggi",
    complexityWhy:
      "Padanan dua hala merentas format penyata pelbagai dan buku tunai, logik perbezaan masa, dan ia mencatat item bank-sahaja ke GL — jadi tahap keseimbangan, idempotensi dan tahap penilaian lebih penting berbanding penanda sehala, walaupun manusia tetap meluluskan catatan.",
    timeToLive: "~beberapa minggu (satu akaun bank / entiti, mod bayang dahulu)",
    bestFit: "Pasukan kewangan yang merekonsiliasi satu atau banyak akaun bank ke lejar setiap hari atau pada hujung bulan",
  },
  outcomes: [
    "Hujung bulan ditutup lebih cepat — rekonsiliasi sedia setiap pagi, bukan kelam-kabut dua hari pada hujung tempoh",
    "Ralat dan penipuan terserlah — perbezaan tidak dijelaskan disiasat, bukan dipaksa seimbang untuk kejar tarikh akhir",
    "Caj bank, faedah dan FX dicatat automatik — tiada lagi mengejar entri bank-sahaja kecil secara manual",
    "Setiap akaun direkonsiliasi konsisten — semakan sama pada setiap penyata, merentas semua akaun bank anda",
  ],
  impact: {
    manualLabel: "Rekonsiliasi akaun secara manual",
    manualValue: "~30–90 min setiap akaun",
    agentLabel: "Dengan ejen",
    agentValue: "~5 min semak (pengecualian sahaja)",
    saved:
      "≈ 25–85 minit setiap akaun setiap kitaran — dan merentas banyak akaun pada hujung bulan itu berhari-hari masa pengawal dikembalikan — sementara satu ralat, bayaran pendua atau debit penipuan yang ditangkap boleh mengatasi seluruh kos AI untuk sebulan.",
    note: "Anggaran; masa dan kadar padanan sebenar bergantung pada bilangan akaun, volum transaksi, sejauh mana bersih rujukan buku tunai anda, dan berapa banyak item bank-sahaja berulang.",
  },
  businessProblem:
    "Kewangan memuat turun setiap penyata bank, membuka buku tunai atau lejar am, dan menanda setiap baris — memadan terimaan dan bayaran dua hala, mengesan caj bank dan faedah yang tidak pernah dicatat, memikirkan cek mana belum tunai dan deposit mana masih dalam transit, dan mengejar sebarang perbezaan yang tidak seimbang. Merentas banyak akaun pada hujung bulan ia meletihkan, dan di bawah tekanan tarikh akhir, godaan ialah memasukkan perbezaan terakhir yang tidak dijelaskan ke baris suspens sekadar untuk menyeimbangkannya. Plug itu tepatnya cara entri hilang, bayaran pendua, atau debit penipuan terkubur — rekonsiliasi sepatutnya menjadi kawalan yang menangkapnya, dan rekonsiliasi manual yang tergesa-gesa ialah tempat kawalan itu senyap gagal.",
  manualWorkflow: {
    steps: [
      "Muat turun setiap penyata bank dan buka buku tunai / GL",
      "Tanda baris yang sepadan dua hala — terimaan dan bayaran",
      "Kesan item bank-sahaja — caj, faedah, FX — tidak pernah dicatat",
      "Fikirkan perbezaan masa — cek belum tunai, deposit dalam transit",
      "Kejar dan jelaskan sebarang perbezaan yang tidak seimbang",
      "Catat pelarasan dan failkan penyata rekonsiliasi",
    ],
    breaks:
      "Bahagian perlahan dan tidak dihargai ialah menanda beratus baris dan memburu perbezaan degil terakhir — dan di bawah tarikh akhir perbezaan itu dimasukkan ke akaun suspens sekadar untuk seimbang, yang tepatnya cara entri hilang atau debit penipuan kekal tersembunyi.",
  },
  agenticWorkflow: {
    narrative:
      "Kami membinanya sebagai aliran evaluator-optimizer: ejen memadan penyata dengan buku tunai, kemudian menilai satu perkara yang penting — adakah rekonsiliasi seimbang kepada sifar, dengan setiap baki dijelaskan? Jika perbezaan tidak dijelaskan, ia berulang kembali untuk memadan semula, mencatat item bank-sahaja dan mengelaskan perbezaan masa, menghalusi sehingga penyata seimbang atau yang tinggal ialah pengecualian bernama dan berbukti untuk manusia — tidak pernah satu plug.",
    steps: [
      "Terima penyata dan buku tunai / GL",
      "Padan setiap baris dua hala",
      "Catat item bank-sahaja; kelaskan perbezaan masa",
      "Nilai: adakah ia seimbang? jelaskan setiap baki",
      "Hasilkan rekonsiliasi; tandakan percanggahan sebenar",
    ],
    inLabel: "Penyata + buku tunai tiba",
    outLabel: "Rekonsiliasi seimbang + pengecualian ditandakan",
    loopAround: {
      index: 3,
      label:
        "Jika rekonsiliasi tidak seimbang, ia berulang kembali untuk memadan semula, memburu entri hilang atau pendua dan mengelaskan semula perbezaan masa — menghalusi sehingga ia seimbang atau baki ialah pengecualian bernama, tidak pernah dimasukkan ke suspens.",
    },
    humanStep: "Pengawal anda menyemak pengecualian dan meluluskan catatan",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Penyata vs buku tunai",
    inMessage:
      "Baki penutup penyata bank RM 128,540.00. Baki buku tunai RM 131,905.00 — perbezaan RM 3,365. 42 baris penyata, 41 baris buku tunai bulan ini.",
    outLabel: "Cara ejen merekonsiliasinya",
    outLines: [
      "40 baris dipadan dua hala; jurang RM 3,365 dijejak ke 3 item rekonsiliasi",
      "Deposit dalam transit RM 3,300 — dibankkan 30 Mac, belum pada penyata; perbezaan masa, bukan ralat",
      "Caj bank RM 105 dan faedah RM 40 — bank-sahaja, dirangka sebagai catatan GL untuk kelulusan",
      "Jambatan: 131,905 − 105 + 40 − 3,300 = 128,540; baki RM 0.00",
    ],
    outResult:
      "Seimbang kepada RM 0 — jurang RM 3,365 dijelaskan sepenuhnya: RM 3,300 masa + RM 65 caj/faedah bersih dicatat. Tiada diplug.",
    note: "Setiap item rekonsiliasi memetik baris penyata dan entri buku tunai · catatan diluluskan oleh pengawal anda · ~RM 0.15–0.40 setiap akaun setiap kitaran dalam kos AI.",
  },
  workflowDetail: [
    {
      title: "Terima penyata dan buku tunai",
      detail:
        "Ejen mengambil penyata bank dalam apa jua bentuk ia tiba — suapan bank (CAMT/MT940), CSV, atau PDF — dan entri buku tunai atau GL untuk tempoh itu, menormalkan kedua-duanya ke bentuk sepunya: tarikh, jumlah, arah, pihak lawan dan rujukan, membaca PDF dengan visi apabila perlu.",
    },
    {
      title: "Padan setiap baris, dua hala",
      detail:
        "Ia memadan baris penyata dengan entri buku tunai dua hala — terimaan dan bayaran — tepat di mana boleh dan kabur pada tarikh, jumlah dan rujukan di mana tidak, jadi bayaran yang direkodkan sehari awal atau di bawah rujukan sedikit berbeza tetap terikat.",
    },
    {
      title: "Catat item bank-sahaja dan kelaskan perbezaan masa",
      detail:
        "Untuk baris pada penyata tetapi bukan buku — caj bank, faedah, FX, pindahan — ia merangka catatan GL untuk kelulusan. Untuk baris dalam buku tetapi belum pada penyata — cek belum tunai, deposit dalam transit — ia mengelaskannya sebagai perbezaan masa dan membawanya ke hadapan, bukan sebagai ralat.",
    },
    {
      title: "Nilai dan halusi (gelung)",
      detail:
        "Ia menyemak satu perkara yang penting: adakah rekonsiliasi kini seimbang kepada sifar dengan setiap baki dijelaskan? Jika tidak, ia berulang kembali — memadan semula, memburu entri hilang atau pendua, menguji semula pengelasan masa — menghalusi sehingga ia seimbang atau perbezaan diasingkan ke pengecualian bernama.",
    },
    {
      title: "Hasilkan rekonsiliasi, tandakan percanggahan",
      detail:
        "Ia mengeluarkan penyata rekonsiliasi — jambatan dari baki buku tunai ke baki bank dengan setiap item rekonsiliasi berbukti — dan senarai pendek percanggahan sebenar untuk dilihat: debit tidak dipadan, pendua, padanan jumlah salah, apa-apa luar corak.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pengawal anda menyemak percanggahan ditandakan dan meluluskan catatan yang dirangka. Tiada apa dicatat ke lejar langsung secara automatik, dan tiada perbezaan pernah diplug untuk memaksa keseimbangan.",
    },
  ],
  howWeBuild:
    "Ia merekonsiliasi kepada sifar atau ia tidak selesai — di mana perbezaan tidak dapat dijelaskan ia memaparkan pengecualian bernama dan bukan memasukkannya ke baris suspens untuk memaksa keseimbangan, yang merupakan seluruh tujuan kawalan itu. Ia bekerja daripada buku tunai langsung anda dan penyata sebenar, memadan terhadap entri sebenar, dan ia mencatat item bank-sahaja hanya sebagai rangka yang pengawal anda luluskan, setiap satu membawa kunci idempotensi supaya menjalankan semula penyata tidak boleh mencatat dua kali caj atau faedah. Setiap item rekonsiliasi — baris dipadan, perbezaan masa, atau percanggahan ditandakan — memetik baris penyata dan entri buku tunai tepat asalnya, jadi rekonsiliasi boleh dibina semula hujung ke hujung.",
  agentLoop: [
    "penyata + buku tunai / GL langsung anda untuk tempoh itu",
    "padan dua hala, catat item bank-sahaja & kelaskan baki (alat MCP)",
    "semak semula rekonsiliasi seimbang kepada sifar sebelum ia selesai",
  ],
  integrations: [
    "Import penyata bank (CAMT / MT940, CSV atau PDF) merentas akaun anda",
    "Lejar am / buku tunai (sistem perakaunan anda)",
    "Pilihan: suapan aplikasi tunai, supaya terimaan pelanggan yang diperuntukkan sepadan bersih",
  ],
  architecture: [
    "Corak: gelung evaluator-optimizer atas Claude Agent SDK — padan → nilai 'adakah ia seimbang dan setiap baki dijelaskan?' → halusi — dengan setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: penyata bank sebenar dan entri buku tunai / GL langsung untuk tempoh itu dimuatkan setiap larian, jadi padanan adalah terhadap entri sebenar dan semasa dan rekonsiliasi mencerminkan buku hari ini dan bukan eksport.",
    "Alat: semakan penyata dan buku tunai baca-sahaja, pemadan baris dua hala (tepat serta kabur pada tarikh/jumlah/rujukan), pengelas perbezaan masa (cek belum tunai, deposit dalam transit), dan satu alat tulis yang hanya merangka catatan GL untuk item bank-sahaja — tiada alat boleh memplug baki suspens atau mengedit entri dipadan.",
    "Get keseimbangan: larian selesai hanya apabila rekonsiliasi seimbang kepada sifar dengan setiap baki dijelaskan, atau apabila perbezaan tidak dijelaskan diasingkan ke pengecualian bernama dan berbukti — ia secara struktur tidak mampu memaksa keseimbangan dengan memplug.",
    "Idempotensi: setiap catatan GL membawa kunci idempotensi (ID baris penyata + jumlah + tarikh) supaya menjalankan semula penyata atau import semula tidak boleh mencatat dua kali caj, faedah atau FX.",
    "Pagar keselamatan: keistimewaan-minimum — ejen merangka catatan dan pengawal meluluskan; ia tidak boleh memindahkan wang, memadam entri, atau menulis di luar set catatan bank-sahaja; output terkunci-skema.",
    "Penyerlahan penipuan & ralat: debit tidak dipadan, pendua, padanan jumlah salah dan item luar corak dibangkitkan sebagai percanggahan dengan bukti, tidak pernah diserap ke dalam angka keseimbangan — tujuan sebenar kawalan.",
    "Kebolehlihatan & audit: setiap padanan, pengelasan masa, catatan dan penyata rekonsiliasi akhir dilog dan boleh dijejak ke baris sumbernya — jejak audit boleh-dibaca-mesin untuk kawalan Kewangan dan audit luar.",
    "Rangka penilaian: set emas rekonsiliasi sejarah sebenar — bersih, berat-masa, dan disemai dengan ralat, pendua dan debit penipuan — dengan semakan keseimbangan deterministik serta LLM-sebagai-hakim pada padanan kabur, diuji regresi sebelum ia mencatat ke GL langsung.",
  ],
  models: [
    {
      task: "Padan & rekonsiliasi penyata ke buku tunai",
      model: "Claude Sonnet",
      why: "Penaakulan dua hala teras merentas tarikh, jumlah dan rujukan berselerak, pada kos sederhana.",
    },
    {
      task: "Padanan baris tepat bervolum tinggi & normalisasi",
      model: "Claude Haiku",
      why: "Kebanyakan baris sepadan tepat; paling murah dan pantas untuk sebahagian besar dan untuk menghurai format penyata.",
    },
    {
      task: "Kelaskan perbezaan masa & item bank-sahaja",
      model: "Claude Sonnet",
      why: "Memutuskan cek-belum-tunai vs deposit-dalam-transit vs kehilangan sebenar memerlukan penaakulan sebenar.",
    },
    {
      task: "Buru perbezaan degil tidak dijelaskan / penipuan disyaki",
      model: "Claude Opus",
      why: "Digunakan hanya apabila rekonsiliasi tidak seimbang selepas penghalusan — sebahagian kecil larian.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap akaun, setiap rekonsiliasi",
        value: "~RM 0.15–0.40",
        note: "penyata + konteks buku tunai + padanan dua hala + gelung keseimbangan + catatan, pada kadar Claude Sonnet/Haiku",
      },
      {
        label: "Penggunaan AI — 50 akaun direkonsiliasi setiap hari",
        value: "~RM 260–700 / bulan",
        note: "lebih rendah dengan Batch API untuk larian hujung bulan waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "menyambung suapan bank dan GL anda adalah beberapa minggu; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding satu bayaran pendua atau debit penipuan yang ditangkap sebulan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada bilangan akaun, volum transaksi dan berapa banyak perbezaan masa berulang. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  modelChoice: {
    approach: "Model tunggal, berperingkat mengikut tugas",
    rationale:
      "Satu rekonsiliasi berkongsi satu konteks — penyata dan buku tunai untuk akaun dan tempoh itu — jadi model tunggal yang lengkap menjalankan padanan dan gelung keseimbangan, dengan Haiku lebih murah pada banyak padanan tepat dan Opus disimpan hanya untuk perbezaan yang tidak selesai. Boleh dipercayai, murah setiap akaun, dan mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk rekonsiliasi satu akaun.",
  },
  evaluation: [
    "Semakan deterministik dahulu — adakah ia seimbang kepada sifar, adakah setiap item rekonsiliasi berbukti, adakah catatan sama dengan jumlah bank-sahaja",
    "Pengesahan sendiri — ejen tidak akan selesai pada perbezaan tidak dijelaskan; ia berulang atau menanda, dan tidak pernah memplug ke suspens",
    "KPI perniagaan — masa setiap rekonsiliasi, peratus auto-rekonsiliasi, kadar perbezaan tidak dijelaskan, hari untuk tutup",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur rekonsiliasi semasa anda pada sampel — masa setiap akaun, sejauh mana konsisten dua akauntan merekonsiliasi akaun sama, dan berapa kerap perbezaan diplug ke suspens hari ini. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia merekonsiliasi dengan betul?",
        metrics: [
          {
            name: "Rekonsiliasi vs akauntan GL kanan",
            measures:
              "Set dipadan, item rekonsiliasi dan percanggahan ditandakannya bersetuju dengan pakar pada akaun terpencil",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah rekonsiliasi kukuh dan lengkap?",
        metrics: [
          {
            name: "Seimbang atau asing — tidak pernah plug",
            measures: "Ia mencapai baki sifar dengan setiap baki dijelaskan, atau mengasingkan pengecualian bernama",
            target: "100%",
          },
          {
            name: "Semakan diperlukan dilaksanakan",
            measures: "Padanan dua hala, catatan bank-sahaja dan pengelasan masa semua berjalan — tiada dilangkau",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan padanan baris",
            measures: "Baris penyata dan buku tunai dipadan benar-benar transaksi sama",
            target: "≥ 99%",
          },
          {
            name: "Recall percanggahan (kadar tangkap)",
            measures: "Daripada ralat, pendua dan debit penipuan yang disemai, berapa yang ditandakan",
            target: "≥ 98%",
          },
          {
            name: "Ketepatan catatan",
            measures: "Catatan bank-sahaja adalah jumlah, tanda dan akaun GL yang betul",
            target: "≥ 99%",
          },
          {
            name: "Tiada keseimbangan dipaksa",
            measures: "Perbezaan tidak dijelaskan tidak pernah diserap ke dalam plug untuk menyeimbangkannya",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak mencatat ke GL langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas rekonsiliasi sejarah — termasuk bulan berat-masa dan ralat, pendua serta debit penipuan yang disemai — berjalan dalam mod bayang dahulu. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas penyata dan buku tunai sejarah sebenar anda — termasuk bulan berat-masa dan, disemai masuk, ralat dan pendua — berjalan dalam mod bayang supaya anda boleh melihat rekonsiliasinya sebelum apa-apa dicatat. Pelancaran satu akaun yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Masih merekonsiliasi akaun bank secara manual?",
  ctaDescription:
    "Beritahu kami berapa akaun anda rekonsiliasi, bank anda dan sistem perakaunan anda, dan kami akan beritahu dengan jujur sama ada ejen rekonsiliasi bank berbaloi dibina — dan berapa banyak ia boleh auto-rekonsiliasi.",
  faqs: [
    {
      question: "Apa bezanya dengan ejen aplikasi tunai?",
      answer:
        "Ejen aplikasi tunai memadankan terimaan pelanggan masuk dengan invois belum jelas (sisi akaun belum terima). Ejen rekonsiliasi bank merekonsiliasi seluruh penyata bank dengan buku tunai atau GL anda — dua hala, caj bank dan faedah, perbezaan masa — dan menghasilkan penyata rekonsiliasi. Ia saling melengkapi: ejen aplikasi tunai boleh menyuap terimaan yang diperuntukkan bersih ke dalam rekonsiliasi.",
    },
    {
      question: "Adakah ia memindahkan wang atau menukar lejar kami sendiri?",
      answer:
        "Tidak. Ia merangka catatan bank-sahaja — caj, faedah, FX — untuk pengawal luluskan, dan ia tidak pernah memindahkan wang, memadam entri, atau memplug baki suspens. Manusia meluluskan setiap catatan; tugas ejen ialah merekonsiliasi dan membukti, bukan menulis ke lejar tanpa penyeliaan.",
    },
    {
      question: "Apa berlaku apabila ia tidak dapat menyeimbangkannya?",
      answer:
        "Ia tidak akan memaksanya. Perbezaan tidak dijelaskan diasingkan dan dipaparkan sebagai pengecualian bernama dan berbukti — entri hilang, pendua, debit mencurigakan — yang tepatnya tujuan kawalan. Ia tidak pernah memplug perbezaan ke suspens untuk kejar tarikh akhir.",
    },
    {
      question: "Bagaimana ia mengendalikan cek belum tunai dan deposit dalam transit?",
      answer:
        "Ia mengelaskannya sebagai perbezaan masa, bukan ralat — menunjukkannya pada rekonsiliasi dan membawanya ke hadapan sehingga ia tunai, dikekalkan berbeza daripada percanggahan sebenar yang memerlukan tindakan.",
    },
    {
      question: "Adakah ia berfungsi merentas Malaysia dan Singapura, pelbagai bank dan mata wang?",
      answer:
        "Ya. Ia merekonsiliasi pelbagai akaun bank, dalam RM dan SGD, merentas format penyata pelbagai (CAMT/MT940/CSV/PDF); perbezaan FX direkonsiliasi dengan kadar ditempah dan toleransi untuk caj. Anggap spesifik cukai dan audit sebagai titik permulaan untuk pasukan kewangan anda, bukan nasihat perakaunan.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep atas satu akaun biasanya beberapa minggu, berjalan dalam mod bayang dahulu; pelancaran penuh merentas akaun mengambil masa lebih lama, kebanyakannya integrasi suapan bank dan GL dan membiasakan pasukan kewangan anda, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "财务与会计",
  title: "银行对账智能体",
  cardSummary:
    "将银行对账单与你的现金账簿逐行核对——双向匹配、登记银行手续费、解释时间性差异，并标记真正的差异。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，将你的银行对账单与现金账簿或总账核对——双向匹配每一行、自动登记手续费与利息等银行方项目、解释未兑现支票等时间性差异，并生成一份平至零的对账表——只把真正的差异呈交你的财务主管处理。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "月末结账不再苦等人工对账，银行手续费与差错当天即被发现，而一笔无法解释的差额——那个可能藏着漏记、重复或欺诈的东西——会被调查，而不是被硬凑平。",
    complexityLabel: "中等–偏高",
    complexityWhy:
      "跨多样对账单格式与现金账簿的双向匹配、时间性差异逻辑，而且它会把银行方项目登记进总账——因此平账门槛、幂等性与评估门槛比单向标记器更为关键，尽管仍由人审批登记。",
    timeToLive: "约数周（单一银行账户 / 实体，先影子模式）",
    bestFit: "每天或月末将一个或多个银行账户核对到总账的财务团队",
  },
  outcomes: [
    "月末结账更快——对账每天早上就绪，而非期末两天的忙乱",
    "差错与欺诈浮现——无法解释的差额会被调查，而非为赶截止硬凑平",
    "银行手续费、利息与 FX 自动登记——不再人工追那些细小的银行方分录",
    "每个账户一致对账——每份对账单同样的检查，覆盖你所有银行账户",
  ],
  impact: {
    manualLabel: "人工对一个账户",
    manualValue: "每账户约 30–90 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 5 分钟复核（仅例外）",
    saved:
      "每个账户每期约省 25–85 分钟——在月末跨多个账户即为数天的财务主管时间——而抓到的一次差错、重复付款或欺诈扣款，就可能超过整月的 AI 成本。",
    note: "为估算；实际时间与匹配率取决于账户数量、交易量、你的现金账簿摘要有多规范，以及有多少银行方项目重复出现。",
  },
  businessProblem:
    "财务下载每份银行对账单、打开现金账簿或总账，逐行勾对——双向匹配收款与付款、找出从未入账的银行手续费与利息、弄清哪些支票尚未兑现、哪些存款仍在途，并追查任何凑不平的差额。月末跨多个账户是一场苦役，而在截止压力下，诱惑就是把最后那笔无法解释的差额挂进暂记账户，只为把它凑平。那一「凑」正是漏记分录、重复付款或欺诈扣款被掩埋的方式——对账本应是抓住它们的控制，而人工、赶工的对账正是这项控制悄悄失效之处。",
  manualWorkflow: {
    steps: [
      "下载每份银行对账单并打开现金账簿 / 总账",
      "双向勾对匹配的行——收款与付款",
      "找出银行方项目——手续费、利息、FX——从未入账的",
      "弄清时间性差异——未兑现支票、在途存款",
      "追查并解释任何凑不平的差额",
      "登记调整并归档对账表",
    ],
    breaks:
      "缓慢又吃力不讨好的部分是勾对成百上千行、并追查最后那笔顽固的差额——而在截止之下，这笔差额被挂进暂记账户只为凑平，这正是漏记分录或欺诈扣款保持隐藏的方式。",
  },
  agenticWorkflow: {
    narrative:
      "我们以 evaluator-optimizer（评估-优化）方式构建：智能体把对账单与现金账簿匹配，然后评估唯一要紧的一件事——对账是否平至零、每一笔残差是否都有解释？若某笔差额无法解释，它会回到上一步重新匹配、登记银行方项目并归类时间性差异，反复精修直到对账平衡，或剩下的是一笔有名有据、交由人处理的例外——绝不「凑」。",
    steps: [
      "接收对账单与现金账簿 / 总账",
      "双向匹配每一行",
      "登记银行方项目；归类时间性差异",
      "评估：是否平衡？解释每一笔残差",
      "生成对账表；标记真正的差异",
    ],
    inLabel: "一份对账单 + 现金账簿到达",
    outLabel: "一份平衡的对账 + 标记的例外",
    loopAround: {
      index: 3,
      label:
        "若对账不平衡，它会回到上一步重新匹配、追查漏记或重复的分录、重新归类时间性差异——精修直到平衡，或残差成为一笔有名有据的例外，绝不挂进暂记账户。",
    },
    humanStep: "你的财务主管审阅例外并审批登记",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "对账单 vs 现金账簿",
    inMessage:
      "银行对账单期末余额 RM 128,540.00。现金账簿余额 RM 131,905.00——差额 RM 3,365。本月 42 行对账单、41 行现金账簿。",
    outLabel: "智能体如何对账",
    outLines: [
      "40 行双向匹配；RM 3,365 的差额追溯到 3 笔对账项",
      "在途存款 RM 3,300——3 月 30 日入行、尚未上对账单；时间性差异，而非差错",
      "银行手续费 RM 105 与利息 RM 40——银行方项目，起草为总账登记待审批",
      "桥接：131,905 − 105 + 40 − 3,300 = 128,540；残差 RM 0.00",
    ],
    outResult:
      "平至 RM 0——RM 3,365 的差额完全解释：RM 3,300 时间性 + RM 65 净手续费/利息已登记。没有任何凑账。",
    note: "每一笔对账项都引用对账单行与现金账簿分录 · 由你的财务主管审批登记 · 每账户每期 AI 成本约 RM 0.15–0.40。",
  },
  workflowDetail: [
    {
      title: "接收对账单与现金账簿",
      detail:
        "智能体以对账单到达的任何形式接收它——银行流水（CAMT/MT940）、CSV 或 PDF——以及该期间的现金账簿或总账分录，把两者规范成统一结构：日期、金额、方向、对手方与摘要，必要时以视觉读取 PDF。",
    },
    {
      title: "双向匹配每一行",
      detail:
        "它双向把对账单行与现金账簿分录匹配——收款与付款——能精确时就精确，不能时则按日期、金额与摘要做模糊匹配，因此一笔早记一天或摘要略有不同的付款仍能对上。",
    },
    {
      title: "登记银行方项目并归类时间性差异",
      detail:
        "对于在对账单上但账上没有的行——银行手续费、利息、FX、转账——它起草一笔总账登记待审批。对于在账上但尚未上对账单的行——未兑现支票、在途存款——它归类为时间性差异并结转，而非差错。",
    },
    {
      title: "评估并精修（循环）",
      detail:
        "它检查唯一要紧的一件事：对账现在是否平至零、每笔残差是否都有解释？若否，它回到上一步——重新匹配、追查漏记或重复的分录、重新检验一项时间性归类——精修直到平衡，或把差额隔离为一笔有名的例外。",
    },
    {
      title: "生成对账表、标记差异",
      detail:
        "它输出对账表——从现金账簿余额到银行余额的桥接、每一笔对账项均有凭据——以及一小份需要关注的真正差异清单：未匹配的扣款、重复、金额错配、任何异常项。",
    },
    {
      title: "交给人处理",
      detail:
        "你的财务主管审阅被标记的差异并审批起草的登记。没有任何内容被自动登记到实时账簿，也从不为凑平而挂账任何差额。",
    },
  ],
  howWeBuild:
    "它要么对平至零、要么不算完成——当某笔差额无法解释时，它呈现一笔有名的例外，而不是挂进暂记账户去硬凑平，而这正是这项控制的全部意义。它基于你的实时现金账簿与真实对账单工作，对照真实分录匹配，并且仅把银行方项目起草为待你财务主管审批的登记，每一笔都带幂等键，因此重跑对账单不会重复登记手续费或利息。每一笔对账项——匹配行、时间性差异或被标记的差异——都引用其来源的确切对账单行与现金账簿分录，因此整份对账可端到端还原。",
  agentLoop: [
    "对账单 + 你该期间的实时现金账簿 / 总账",
    "双向匹配、登记银行方项目并归类残差（MCP 工具）",
    "在完成前复核对账是否平至零",
  ],
  integrations: [
    "银行对账单导入（CAMT / MT940、CSV 或 PDF），覆盖你的各账户",
    "总账 / 现金账簿（你的会计系统）",
    "可选：现金核销的输入，让已核销的客户收款干净对上",
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的评估-优化循环——匹配 → 评估「是否平衡、每笔残差是否都有解释？」→ 精修——每个工具通过进程内 MCP 服务器提供。",
    "上下文：真实的银行对账单与该期间的实时现金账簿/总账分录按次载入，因此匹配对照的是真实、当前的分录，对账反映今天的账，而非导出文件。",
    "工具：只读的对账单与现金账簿查询、双向行匹配器（精确加按日期/金额/摘要的模糊）、时间性差异归类器（未兑现支票、在途存款），以及一个仅为银行方项目起草总账登记的写入工具——没有任何工具能挂平暂记余额或修改已匹配分录。",
    "平账门槛：仅当对账平至零且每笔残差都有解释、或无法解释的差额被隔离为一笔有名有据的例外时，本次运行才算完成——它在结构上无法通过挂账去硬凑平。",
    "幂等：每笔总账登记都带幂等键（对账单行 ID + 金额 + 日期），使重跑对账单或重复导入不会重复登记手续费、利息或 FX。",
    "防护机制：最小权限——智能体起草登记、由主管审批；它不能移动资金、删除分录，或在银行方登记集之外写入；模式锁定输出。",
    "欺诈与差错浮现：未匹配的扣款、重复、金额错配与异常项都会连同证据作为差异提出，绝不被吸收进一个平账数字——这正是这项控制的真正目的。",
    "可观测性与审计：每次匹配、时间性归类、登记以及最终对账表都会留痕并可追溯到其来源行——一条用于财务管控与外部审计的机器可读审计轨迹。",
    "评估框架：一组真实历史对账的黄金集——干净、时间性差异密集、并植入差错/重复/欺诈扣款——配合确定性平账检查，并对模糊匹配采用 LLM 作为评审，每次改动在登记到实时总账前都做回归测试。",
  ],
  models: [
    {
      task: "匹配并把对账单对到现金账簿",
      model: "Claude Sonnet",
      why: "跨日期、金额与杂乱摘要的核心双向推理，成本适中。",
    },
    {
      task: "高频精确行匹配与规范化",
      model: "Claude Haiku",
      why: "大多数行精确匹配；处理大批量及解析对账单格式最便宜、最快。",
    },
    {
      task: "归类时间性差异与银行方项目",
      model: "Claude Sonnet",
      why: "判断未兑现支票 vs 在途存款 vs 真正漏记，需要真实推理。",
    },
    {
      task: "追查顽固的无法解释差额 / 疑似欺诈",
      model: "Claude Opus",
      why: "仅在精修后仍无法平衡时调用——占运行的一小部分。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每账户每次对账",
        value: "约 RM 0.15–0.40",
        note: "对账单 + 现金账簿上下文 + 双向匹配 + 平账循环 + 登记，按 Claude Sonnet/Haiku 费率",
      },
      {
        label: "AI 用量——每天核对 50 个账户",
        value: "约 RM 260–700 / 月",
        note: "用 Batch API 处理夜间月末任务可更低",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "接入你的银行流水与总账约需数周；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比每月抓到的一次重复付款或欺诈扣款，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于账户数量、交易量与有多少时间性差异重复出现。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  modelChoice: {
    approach: "单一模型，按任务分级",
    rationale:
      "一次对账共享同一上下文——该账户该期间的对账单与现金账簿——因此由一个完善配置的单一模型来做匹配与平账循环，用更便宜的 Haiku 处理大量精确匹配，把 Opus 仅留给对不平的差额。可靠、每账户成本更低、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非单个账户的对账。",
  },
  evaluation: [
    "先做确定性检查——是否平至零、每笔对账项是否有凭据、登记是否等于银行方合计",
    "自我验证——智能体不会在无法解释的差额上收工；它循环或标记，绝不挂进暂记",
    "业务 KPI——每次对账耗时、自动对账比例、无法解释差额率、结账天数",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量你当前的对账——每个账户耗时、两名会计对同一账户对账的一致程度，以及目前有多少差额被挂进暂记。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "它是否正确地完成了对账？",
        metrics: [
          {
            name: "对账（对比资深总账会计）",
            measures: "在留出账户上，其匹配集、对账项与被标记差异与专家一致",
            target: "≥ 98%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "对账是否合理且完整？",
        metrics: [
          {
            name: "平衡或隔离——绝不凑账",
            measures: "它达到零余额且每笔残差都有解释，或隔离出一笔有名的例外",
            target: "100%",
          },
          {
            name: "必需检查全部执行",
            measures: "双向匹配、银行方登记与时间性归类全部运行——不遗漏",
            target: "100%",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "行匹配准确率",
            measures: "匹配的对账单行与现金账簿行确为同一笔交易",
            target: "≥ 99%",
          },
          {
            name: "差异召回率（拦截率）",
            measures: "植入的差错、重复与欺诈扣款中，有多少被标记出来",
            target: "≥ 98%",
          },
          {
            name: "登记准确率",
            measures: "银行方登记的金额、方向与总账科目正确",
            target: "≥ 99%",
          },
          {
            name: "无强行凑平",
            measures: "无法解释的差额绝不被吸收进一个凑平数字",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史对账黄金集（含时间性差异密集的月份与植入的差错、重复、欺诈扣款）上超过人工基线并达到这些 GPA 目标之前，它不会登记到实时总账，且先以影子模式运行。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史对账单与现金账簿上做概念验证——包括时间性差异密集的月份，以及植入的差错与重复——以影子模式运行，让你在任何内容被登记前先看到它的对账。聚焦单一账户的落地只需数周。",
  ctaTitle: "还在用人工对银行账户吗？",
  ctaDescription:
    "告诉我们你要核对多少个账户、开户银行与会计系统，我们会诚实告诉你银行对账智能体是否值得构建，以及它能自动对平多少。",
  faqs: [
    {
      question: "它和现金核销智能体有什么区别？",
      answer:
        "现金核销智能体把入账的客户收款匹配到未清发票（应收一侧）。银行对账智能体把整份银行对账单与你的现金账簿或总账核对——双向、银行手续费与利息、时间性差异——并生成对账表。两者互补：现金核销智能体可以把已干净核销的收款输入到对账中。",
    },
    {
      question: "它会自行移动资金或改动我们的账簿吗？",
      answer:
        "不会。它把银行方项目——手续费、利息、FX——起草为待主管审批的登记，且绝不移动资金、删除分录或挂平暂记余额。每一笔登记都由人审批；智能体的职责是对账与举证，而非无人监督地写入账簿。",
    },
    {
      question: "当它无法对平时会怎样？",
      answer:
        "它不会硬凑。无法解释的差额会被隔离并作为一笔有名有据的例外呈现——漏记的分录、重复、可疑的扣款——这正是这项控制的意义所在。它绝不为赶截止而把差额挂进暂记。",
    },
    {
      question: "它如何处理未兑现支票与在途存款？",
      answer:
        "它把它们归类为时间性差异，而非差错——在对账中列示并结转直至兑现，与需要处理的真正差异保持区分。",
    },
    {
      question: "它能跨马来西亚和新加坡、多家银行与多币种吗？",
      answer:
        "可以。它对多个银行账户、以 RM 与 SGD、跨多样对账单格式（CAMT/MT940/CSV/PDF）对账；FX 差异按入账汇率并对手续费设容差来核对。请将税务与审计细节视为你财务团队的起点，而非会计意见。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一账户的概念验证通常只需数周，先以影子模式运行；跨账户的全面落地需要更长时间，主要是银行流水与总账集成以及让财务团队适应，而不是 AI 本身。",
    },
  ],
};

export const bankReconciliation: AgentUseCase = {
  structure: {
    slug: "bank-reconciliation",
    status: "available",
    pattern: "evaluator-optimizer",
    route: "solutions/agent-use-cases/bank-reconciliation",
    heroImage: "/images/agent-use-cases/bank-reconciliation-hero.jpg",
    cardImage: "/images/agent-use-cases/bank-reconciliation-card.jpg",
    relatedUseCases: ["cash-application", "invoice-matching"],
    trackLocation: "agent_use_case_bank_reconciliation",
    valueScore: 4,
    complexityScore: 4,
  },
  content: { en, ms, zh },
};
